import React, { useEffect, useState, useCallback, useRef } from 'react';
import 'moment/locale/es';
import apiClient from '../../utils/axiosPetition';
import { parseAppointmentDate } from '../../utils/appointmentDate';
import CitasControl from '../AdminComponent/CitasControl';
import { alertaToastTopEnd, alertaGeneral } from '../../utils/alertas';
import { getVideoAccessStatus } from '../../utils/appointmentAccessWindow';
import { useNavigate, useLocation } from 'react-router-dom';
import AgendarTeleconsulta from '../DoctorComponent/AgendarTeleconsulta';
import { useDataUser } from '../../context/dataUserContext';
import CustomModal from '../GlobalComponents/CustomModal';
import { formatServiceName } from '../../utils/formatServiceName';

const PatientControl = () => {
    const [error, setError] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [allAppointments, setAllAppointments] = useState([]);
    const [cancelReason, setCancelReason] = useState('');
    const [cargando, setCargando] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', body: null, confirmText: '', onConfirm: null });
    const pollingIntervalRef = useRef(null);

    const { patientId } = useDataUser();
    const navigate = useNavigate();
    const location = useLocation();

    const fetchAllAppointments = useCallback(async () => {
        try {
            if (!patientId) {
                throw new Error('Falta información de autenticación');
            }

            const response = await apiClient.get(`/api/clinic/getAllAppointmentsPatient/${patientId}`);

            console.log('Datos de citas recibidos del backend:', response.data);

            const allAppointmentsReturn = response.data.map(appointment => {
                return {
                    ...appointment,
                    date: parseAppointmentDate(appointment.date),
                    service: appointment.doctor?.service
                        ? formatServiceName(appointment.doctor.service)
                        : (appointment.service ? formatServiceName(appointment.service) : 'Servicio no disponible'),
                }
            });

            // Ordenar por fecha/hora: las citas más próximas primero
            allAppointmentsReturn.sort((a, b) => a.date.valueOf() - b.date.valueOf());

            setAllAppointments(allAppointmentsReturn);
            setCargando(false);
            setError(null);
        } catch (error) {
            console.error('Error fetching all appointments:', error);
            setCargando(false);
            setError(error.message || 'Hubo un error al cargar todas las citas');
        }
    }, [patientId]);

    useEffect(() => {
        if (patientId) {
            fetchAllAppointments();
            
            // Configurar polling cada 30 segundos para actualizar las citas en la página
            pollingIntervalRef.current = setInterval(() => {
                fetchAllAppointments();
            }, 30000); // 30 segundos

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
            };
        }
    }, [patientId, fetchAllAppointments]);

    const updateAllAppointments = async () => {
        setCargando(true);
        try {
            await fetchAllAppointments();
        } catch (error) {
            console.error('Error al actualizar citas:', error);
            setCargando(false);
            setError(error.message || "Error inesperado, intente de nuevo");
        }
    };

    const handleVideoCall = (appointmentId, firmoConsentimiento, isProfileComplete, appointmentDate) => {
        if (!isProfileComplete) {
            setModalContent({
                title: 'Perfil Incompleto',
                body: <p>Para poder unirte a la videoconsulta, debes completar tu perfil primero.</p>,
                confirmText: 'Completar Perfil',
                onConfirm: () => {
                    setModalOpen(false);
                    navigate('/patient/perfil');
                }
            });
            setModalOpen(true);
            return;
        }

        if (firmoConsentimiento === 'No') {
            setModalContent({
                title: 'Consentimiento Informado Requerido',
                body: (
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: '1.1em', marginBottom: 10, textAlign: 'center' }}>
                        SERVICIOS DE LA RED COLOMBIANA DE PRESTACIÓN Y CUIDADOS EN SALUD
                      </h3>
                      <h4 style={{ fontWeight: 600, fontSize: '1em', marginBottom: 18, textAlign: 'center' }}>
                        CONSENTIMIENTO INFORMADO
                      </h4>
                      <p>
                        Para acceder a la teleconsulta, es indispensable que diligencies el consentimiento informado. Este documento garantiza que comprendes y aceptas las condiciones bajo las cuales se prestará el servicio de salud a distancia.
                      </p>
                      <ol style={{ marginTop: 16, marginBottom: 16, paddingLeft: '1.2em' }}>
                        <li>
                          <b>Lee la descripción de la modalidad de consulta:</b> Explica en qué consiste la prestación de servicios de salud a distancia, conforme a la Ley 1419 de 2010.
                        </li>
                        <li>
                          <b>Revisa la declaración del usuario:</b> Autoriza el uso, almacenamiento y transmisión de tu información personal y médica, así como el uso de equipos electrónicos y multimedia necesarios para la atención.
                        </li>
                        <li>
                          <b>Lee la declaración de satisfacción:</b> Manifiesta que has comprendido la naturaleza y propósito del servicio, y que has tenido oportunidad de resolver tus dudas.
                        </li>
                        <li>
                          <b>Revisa la extensión del consentimiento:</b> Autoriza el uso de tu información para fines de investigación médica bajo estrictas condiciones de confidencialidad y seguridad.
                        </li>
                        <li>
                          <b>Si estás de acuerdo, continúa en "Diligenciar consentimiento"</b> para completar y firmar el formulario correspondiente.
                        </li>
                      </ol>
                      <p style={{ marginBottom: 0 }}>
                        <b>Nota:</b> Si tienes inquietudes adicionales, puedes comunicarte con nuestro equipo de soporte para recibir orientación.<br />
                        <b>Al diligenciar y firmar el consentimiento, autorizas de manera voluntaria la prestación del servicio de Telemedicina bajo los términos descritos.</b>
                      </p>
                    </div>
                ),
                confirmText: 'Consentimiento Interno',
                onConfirm: () => {
                    setModalOpen(false);
            navigate(`/consentimiento`, { state: { appointmentId } });
                },
                cancelText: 'Consentimiento Digital',
                onCancel: () => {
                    setModalOpen(false);
                    navigate(`/consentimiento-externo?appointmentId=${appointmentId}`);
                }
            });
            setModalOpen(true);
            return;
        }

        if (firmoConsentimiento === 'Si') {
            const access = getVideoAccessStatus(appointmentDate?.toISOString?.() || appointmentDate);
            if (!access.canAccess) {
                alertaGeneral({
                    titulo: access.code === 'TOO_EARLY' ? 'Aún no es hora de la consulta' : 'Videoconsulta no disponible',
                    messageHtml: access.message,
                    isWarning: true,
                });
                return;
            }

            setModalContent({
                title: 'Recomendaciones para una Teleconsulta Exitosa',
                body: (
                    <div>
                      <ul>
                        <li><b>Ubicación:</b> Elige un lugar tranquilo, privado y bien iluminado para tu consulta.</li>
                        <li><b>Cámara y micrófono:</b> Asegúrate de que tu cámara y micrófono funcionen correctamente y estén habilitados en tu dispositivo.</li>
                        <li><b>Conexión a internet:</b> Verifica que cuentas con una conexión a internet estable para evitar interrupciones.</li>
                        <li><b>Documentos y datos:</b> Ten a la mano tu documento de identidad y cualquier información médica relevante.</li>
                        <li><b>Puntualidad:</b> Conéctate a la plataforma unos minutos antes de la hora agendada para realizar las pruebas técnicas necesarias.</li>
                      </ul>
                      <p style={{ marginTop: 18, marginBottom: 0 }}>
                        Si tienes alguna dificultad técnica, comunícate con nuestro equipo de soporte.
                      </p>
                    </div>
                ),
                confirmText: 'Entendido',
                onConfirm: () => {
                    setModalOpen(false);
            navigate(`/video-call/${appointmentId}`, { state: { isPatient: true } });
                }
            });
            setModalOpen(true);
        }
    };

    const handleCancel = (appointmentId) => {
        setSelectedAppointment(appointmentId);
    };

    const confirmCancelWithReason = async () => {

        try {
            await apiClient.put(`/api/clinic/cancelAppointment/${selectedAppointment}`, {
                motivoCancelacion: cancelReason,
                canceladoPor: 'patient',
            });

            fetchAllAppointments();
            setCancelReason('');
            alertaToastTopEnd({
                mensaje: 'Cita cancelada',
            })
            return true;
        } catch (error) {
            console.error('Error al cancelar la cita:', error);
        }
    };

    const fromPayment = location.state?.fromPayment === true;

    return (
        <div className="space-y-4 md:space-y-5 px-2 md:px-0">
            <CitasControl
                cargando={cargando}
                isPatient={true}
                error={error}
                allAppointments={allAppointments}
                handleVideoCall={handleVideoCall}
                handleCancel={handleCancel}
                updateAllAppointments={updateAllAppointments}
                cancelReason={cancelReason}
                setCancelReason={setCancelReason}
                onConfirmCancelWithReason={confirmCancelWithReason}
                highlightTitle={fromPayment}
            />

            <AgendarTeleconsulta />

            <CustomModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={modalContent.title}
                confirmText={modalContent.confirmText}
                onConfirm={modalContent.onConfirm}
            >
                {modalContent.body}
            </CustomModal>
        </div>
    );
};

export default PatientControl;